import { createSelector } from 'reselect';
import { selectors as macheteReduxSelectors } from 'src/machete-redux';
import constants from 'src/constants';
import { get as lodashGet } from 'lodash';
import designSidebarUtils from 'src/utils/designSidebarUtils';
import productCatalogUtils from 'src/utils/productCatalogUtils';

const {
  shouldShowManufacturerForMaterial,
  shouldShowProduct,
  shouldShowVariationGroup,
} = designSidebarUtils;

const {
  CATEGORIES,
  GLASS_ID,
  MATERIALS_ARRAY,
  PAINT_MANUFACTURERS,
  MATERIALS,
  SLIDING_GLASS_DOOR_ID,
  PATIO_DOOR_ID,
  THREE_D_OPENING_MATERIAL_IDS,
} = constants.labels;
const {
  ELEMENT_PAINT_ATTRIBUTES,
  ELEMENT_STYLE_ATTRIBUTES,
  MODE_NAMES,
  PAINT_BRAND_ATTRIBUTE_PROPERTY,
  PRODUCT_ATTRIBUTE_PROPERTY,
} = constants.app;

const getDesignProFlipperFlags = (state) => {
  return (
    lodashGet(state, 'designPro.app.jobDetails.machete_blob.DesignPro') || {}
  );
};
const getDesignProFeatureAccesses = (state) => {
  return (
    lodashGet(state, 'designPro.app.jobDetails.through_org.features') || []
  );
};
const getOrgFeatureAccess = (state) => {
  return lodashGet(state, 'designPro.user.userProfile.orgs[0].features') || [];
};
const getActiveEditingAttribute = (state) =>
  state.designPro.app.activeEditingAttribute;
const getActiveEditingStyle = (state) => state.designPro.app.activeEditingStyle;
const getIsEditing3DOpening = createSelector(
  getActiveEditingStyle,
  getActiveEditingAttribute,
  (activeStyle, activeAttribute) =>
    THREE_D_OPENING_MATERIAL_IDS.includes(activeStyle.materialId) &&
    activeAttribute.property === 'colorId',
);
const getActiveMode = (state) => state.designPro.app.activeMode;
const getColorSelectionCount = (state) =>
  state.designPro.app.numberOfColorSelections;
const getShareToken = (state) => state.designPro.app.shareToken;
const getBreadcrumbData = (state) => state.designPro.app.breadcrumbData;
const getColorByIdProp = (state, { colorId }) =>
  state.designPro.app.variations[colorId];
const getPaintColorByIdProp = (state, { paintColorId }) =>
  state.designPro.app.variations[paintColorId];
const getMacheteProducts = (state) => state.designPro.app.macheteProducts;
const getMacheteVariationGroups = (state) =>
  state.designPro.app.variationGroups;
const getMacheteVariations = (state) => state.designPro.app.variations;
const getProductByIdProp = (state, { productId }) =>
  state.designPro.app.macheteProducts[productId];
const getErrorModalType = (state) => state.designPro.app.errorModalType;
const getIsScreenshotModeActive = (state) =>
  state.designPro.app.isScreenshotModeActive;
const isProductCatalogInitialized = (state) =>
  state.designPro.app.isProductCatalogInitialized;
const isSelectToolActive = (state) => state.designPro.app.isSelectToolActive;
const getNodeStyles = (state) => state.designPro.app.nodeStyles;
const getJobDetails = (state) => state.designPro.app.jobDetails;

// The following are needed for exported selectors
const getLabels = (state) => state.designPro.machete.labels;
const getManufacturerByIdProp = (state, { brandId }) =>
  state.designPro.machete.manufacturers[brandId];
const getPaintBrandByIdProp = (state, { paintBrandId }) =>
  state.designPro.machete.manufacturers[paintBrandId];
const getManufacturers = (state) => state.designPro.machete.manufacturers;
const getMaterialByIdProp = (state, { materialId }) =>
  state.designPro.machete.labels[materialId];
const getProducts = (state) => state.designPro.machete.products;

// Done
const shouldShowDesignProSpinner = (state) => {
  const { loading, initialized } = state.machete.model;
  const { isSwitchingDesign, isSavingDesign } = state.designPro.jobDesigns;
  return loading || isSwitchingDesign || isSavingDesign || !initialized;
};

const isBrandPaintProvider = (brandId) => PAINT_MANUFACTURERS.includes(brandId);
const shouldShowPaintBrandsForMaterial = (manufacturer, materialId) =>
  productCatalogUtils.isPaintableMaterial(materialId) &&
  isBrandPaintProvider(manufacturer.id);

const getActiveRenderMode = createSelector(
  getActiveMode,
  () => 'rasterization',
);

const getActiveAttributes = createSelector(
  getActiveEditingAttribute,
  (activeAttribute) => {
    const isPaint = ELEMENT_PAINT_ATTRIBUTES.includes(activeAttribute);
    return isPaint ? ELEMENT_PAINT_ATTRIBUTES : ELEMENT_STYLE_ATTRIBUTES;
  },
);

const getActiveEditingAttributeIndex = createSelector(
  getActiveEditingAttribute,
  getActiveAttributes,
  (activeAttribute, activeAttributes) =>
    activeAttributes.findIndex(
      ({ property }) => property === activeAttribute.property,
    ),
);

const isDesignModeActive = createSelector(
  getActiveMode,
  (activeMode) => activeMode === MODE_NAMES.DESIGN,
);

const getNextActiveAttribute = createSelector(
  getActiveEditingAttributeIndex,
  getActiveEditingAttribute,
  getActiveEditingStyle,
  getActiveAttributes,
  (activeAttributeIndex, activeAttribute, activeStyle, activeAttributes) => {
    const isProductActive =
      activeAttribute.property === PRODUCT_ATTRIBUTE_PROPERTY;
    const isColorInStyle = activeStyle.paintColorId;
    if (isProductActive && isColorInStyle) return null;

    return activeAttributes[activeAttributeIndex + 1];
  },
);

const getActiveEditingAttributePropertyId = (state) => {
  const activeEditingAttribute = getActiveEditingAttribute(state);
  const activeEditingStyle = getActiveEditingStyle(state);
  return activeEditingStyle[activeEditingAttribute.property];
};

const getItemForAttributeSelectionButton = (state, { style, attribute }) => {
  if (style[attribute.property]) {
    switch (attribute.property) {
      case 'materialId':
        return getMaterialByIdProp(state, style);
      case 'brandId':
        return getManufacturerByIdProp(state, style);
      case 'productId':
        return getProductByIdProp(state, style);
      case 'colorId':
        return getColorByIdProp(state, style);
      case 'paintBrandId':
        return getPaintBrandByIdProp(state, style);
      case 'paintColorId':
        return getPaintColorByIdProp(state, style);
      default:
    }
  }
};

const getImageUrlForItem = (
  options = { useSmallerImage: false },
  item,
  attribute,
) => {
  const { useSmallerImage } = options;
  const { property } = attribute;

  switch (property) {
    case 'materialId':
      return item.imageUrl;
    case 'brandId':
    case 'paintBrandId':
      return useSmallerImage ? item.sm_logo_url : item.logo_url;
    case 'productId':
    case 'colorId':
      return item.texture && item.texture.thumbnail
        ? item.texture.thumbnail.url
        : '';
    default:
      return '';
  }
};

const isSelectionAvailable = (state, style, attribute) => {
  const labels = getLabels(state);
  const manufacturers = getManufacturers(state);
  const products = getProducts(state);
  const { materialId, brandId, productId } = style;

  switch (attribute.property) {
    case 'brandId':
      return labels[materialId] ? labels[materialId].hasBrands : true;
    case 'productId':
      return manufacturers[brandId] ? manufacturers[brandId].hasProducts : true;
    case 'colorId':
      return products[productId] ? products[productId].hasColors : true;
    default:
      return true;
  }
};

const getTextForPaintAttribute = ({
  available,
  isSelected,
  isExtendedSidebarOpen,
  item,
  callToAction,
  placeholder,
}) => {
  const { name } = item;

  if (available) {
    return isSelected && isExtendedSidebarOpen
      ? placeholder
      : name || callToAction || placeholder;
  }
  return 'Not Available';
};

const getTextForStyleAttribute = ({
  available,
  isSelected,
  isExtendedSidebarOpen,
  item,
  placeholder,
}) => {
  const { name } = item;

  if (available) {
    return isSelected && isExtendedSidebarOpen
      ? placeholder
      : name || placeholder;
  }

  return 'Not Available';
};

const getDataForAttributeSelectionButton = (state, { style, attribute }) => {
  const activeEditingStyle = getActiveEditingStyle(state);
  const activeEditingAttribute = getActiveEditingAttribute(state);
  const { isExtendedSidebarOpen } = state.designPro.ui;
  const isSelected =
    style.styleKey === activeEditingStyle.styleKey &&
    attribute.property === activeEditingAttribute.property;
  const options = { useSmallerImage: true };
  const item =
    getItemForAttributeSelectionButton(state, { style, attribute }) || {};
  const imageUrl = getImageUrlForItem(options, item, attribute);
  const available = isSelectionAvailable(state, style, attribute);
  const hasSelection = !!item.name;
  const isCallToAction =
    attribute.property === 'brandId' && !hasSelection && !isExtendedSidebarOpen;
  const isOverridden = attribute.property === 'colorId' && !!style.paintColorId;
  const shouldDim = (!isSelected && isExtendedSidebarOpen) || isOverridden;
  const paintAttributeIds = ELEMENT_PAINT_ATTRIBUTES.map(
    (attr) => attr.property,
  );
  const showClearPaintButton =
    attribute.property === PAINT_BRAND_ATTRIBUTE_PROPERTY && style.paintBrandId;
  const isPaintAttribute = paintAttributeIds.includes(attribute.property);
  const dataForText = {
    available,
    isSelected,
    isExtendedSidebarOpen,
    item,
    placeholder: attribute.placeholder,
    callToAction: attribute.callToAction,
  };
  const text = isPaintAttribute
    ? getTextForPaintAttribute(dataForText)
    : getTextForStyleAttribute(dataForText);

  return {
    ...item,
    available,
    hasSelection,
    imageUrl,
    isCallToAction,
    isOverridden,
    isSelected,
    shouldDim,
    showClearPaintButton,
    text,
  };
};

const getManufacturersByMaterialId = (state, { materialId }) => {
  const manufacturers = getManufacturers(state);
  return Object.values(manufacturers)
    .filter((manufacturer) =>
      shouldShowManufacturerForMaterial(manufacturer, materialId),
    )
    .map((manufacturer) => ({
      ...manufacturer,
      imageUrl: getImageUrlForItem({}, manufacturer, { property: 'brandId' }),
    }));
};

const getPaintBrands = (state, { materialId }) => {
  const manufacturers = getManufacturers(state);
  return Object.values(manufacturers)
    .filter((manufacturer) =>
      shouldShowPaintBrandsForMaterial(manufacturer, materialId),
    )
    .map((manufacturer) => ({
      ...manufacturer,
      imageUrl: getImageUrlForItem({}, manufacturer, {
        property: 'brandId',
      }),
    }));
};

const getPaintColors = (state, { paintProductId }) => {
  const variations = getMacheteVariations(state);
  const attribute = getActiveEditingAttribute(state);
  const products = getMacheteProducts(state);
  const product = products[paintProductId] || {};

  const items = product.hasColors ? product.children : [];

  return items.map((item) => {
    const texture = variations[item.id] || {};
    return {
      ...item,
      imageUrl: getImageUrlForItem({}, texture, attribute),
    };
  });
};

const getMaterialsWithBrands = createSelector(
  getManufacturers,
  (manufacturers) =>
    Object.values(manufacturers).reduce(
      (acc, { label_ids = [] }) => new Set([...acc, ...label_ids]),
      new Set(),
    ),
);

// material selection is only allowed for wall and door materials
const getValidMaterials = createSelector(
  getLabels,
  getMaterialsWithBrands,
  getActiveEditingStyle,
  (labels, materialsWithBrands, activeStyle) => {
    const checkIsDoorMaterial = (materialId) =>
      [PATIO_DOOR_ID, SLIDING_GLASS_DOOR_ID].includes(materialId);
    const isEditingWalls = activeStyle.category === CATEGORIES.WALLS;
    const isEditingDoors = checkIsDoorMaterial(activeStyle.materialId);
    let materials = [];

    if (isEditingWalls) {
      materials = MATERIALS_ARRAY.filter(
        ({ category }) => category === CATEGORIES.WALLS,
      );
    }

    if (isEditingDoors) {
      materials = MATERIALS_ARRAY.filter(({ id }) => checkIsDoorMaterial(id));
    }

    return materials
      .filter(({ id }) => materialsWithBrands.has(id))
      .map(({ id }) => labels[id]);
  },
);

const getProductsByBrandAndMaterial = (state, { brandId, materialId }) => {
  const products = getMacheteProducts(state);
  return Object.values(products)
    .filter(({ manufacturer_ids = new Set(), label_id }) => {
      return label_id === materialId && manufacturer_ids.has(brandId);
    })
    .filter(shouldShowProduct)
    .map((product) => ({
      ...product,
      imageUrl: getImageUrlForItem({}, product, { property: 'productId' }),
    }));
};

const getColorsByProductId = (state, { productId }) => {
  const { shouldShowVariation } = designSidebarUtils;
  const products = getMacheteProducts(state);
  const variations = getMacheteVariations(state);
  const attribute = getActiveEditingAttribute(state);
  const product = products[productId] || {};
  const items = product.hasColors ? product.children : [];

  return items.filter(shouldShowVariation).map((item) => {
    const texture = variations[item.id] || {};
    return {
      ...item,
      imageUrl: getImageUrlForItem({}, texture, attribute),
    };
  });
};

const getExtendedSidebarItems = (state) => {
  const style = getActiveEditingStyle(state);
  const attribute = getActiveEditingAttribute(state);

  switch (attribute.property) {
    case 'materialId':
      return getValidMaterials(state);
    case 'brandId':
      return getManufacturersByMaterialId(state, style);
    case 'productId':
      return getProductsByBrandAndMaterial(state, style);
    case 'colorId':
      return getColorsByProductId(state, style);
    case 'paintBrandId':
      return getPaintBrands(state, style);
    case 'paintColorId':
      return getPaintColors(state, style);
    default:
      return [];
  }
};

const isActiveEditingAttributeIsColor = createSelector(
  getActiveEditingAttribute,
  ({ property }) => property === 'colorId',
);

const isActiveEditingAttributePaintColor = createSelector(
  getActiveEditingAttribute,
  ({ property }) => property === 'paintColorId',
);

const colorPropertyHasVariationGroups = ({ children }) =>
  children && children.length > 0;
// Done
const getActiveVariationGroups = createSelector(
  getProducts,
  getMacheteProducts,
  getActiveEditingStyle,
  (products, macheteProducts, { productId, paintProductId }) => {
    const id = paintProductId || productId;
    const { shouldShowVariation } = designSidebarUtils;
    return macheteProducts[id].children
      .filter(({ children }) => children.length > 0)
      .filter(shouldShowVariationGroup)
      .map((variationGroup) => ({
        ...variationGroup,
        children: variationGroup.children
          .filter(shouldShowVariation)
          .map((variation) => {
            const variationProductId = variation.product_id;
            variation.texture = !variation.color
              ? lodashGet(products, `[${variationProductId}].texture`)
              : '';

            return {
              ...variation,
              imageUrl: getImageUrlForItem({}, variation, {
                property: 'colorId',
              }),
            };
          }),
      }));
  },
);

const getActiveVariationGroupIndex = createSelector(
  getActiveVariationGroups,
  getActiveEditingAttributePropertyId,
  (variationGroups, colorId) => {
    const selectedColor = variationGroups.findIndex(({ children }) =>
      children.find(({ id }) => id === colorId),
    );
    // if the selected color is not found in current brand, id will be negative, set to zero
    return selectedColor > -1 ? selectedColor : 0;
  },
);

const getExtendedSidebarData = createSelector(
  getExtendedSidebarItems,
  isActiveEditingAttributeIsColor,
  isActiveEditingAttributePaintColor,
  (items, isEditingColor, isEditingPaintColor) => ({
    items,
    shouldShowVariationGroups:
      items.length > 0 &&
      (isEditingColor || isEditingPaintColor) &&
      colorPropertyHasVariationGroups(items[0]),
  }),
);

const getNodeStylesMap = createSelector(getNodeStyles, (nodes) =>
  nodes.reduce((acc, node) => {
    acc[node.uuid] = node;
    return acc;
  }, {}),
);

const getSelectedNodeStyles = createSelector(
  getNodeStylesMap,
  macheteReduxSelectors.segmentation.getSelectedNodeIds,
  (stylesMap, selectedNodeIds) =>
    selectedNodeIds.map((uuid) => stylesMap[uuid]),
);

const generateStyleKey = ({
  materialId = '',
  brandId = '',
  productId = '',
  colorId = '',
  paintBrandId = '',
  paintProductId = '',
  paintColorId = '',
}) =>
  `${materialId}-${brandId}-${productId}-${colorId}-${paintBrandId}-${paintProductId}-${paintColorId}`;

/*
A Unique Element Style is defined as a unique combinations of material,
brand, product, and color that exist on a model.

A model will have N number of Unique Element Styles and each will be
associated with a group of nodes
*/
const getUniqueSelectedElementStyles = createSelector(
  getSelectedNodeStyles,
  (selectedNodes) => {
    return selectedNodes.reduce((acc, { uuid, ...nodeStyle }) => {
      // See NS-748 User should not see "Glass" in selected element styles
      if (nodeStyle.materialId === GLASS_ID) return acc;

      const styleKey = generateStyleKey(nodeStyle);

      if (!acc[styleKey]) {
        acc[styleKey] = {
          nodes: [uuid],
          styleKey,
          ...nodeStyle,
        };
      } else {
        acc[styleKey].nodes.push(uuid);
      }

      return acc;
    }, {});
  },
);

// the category is either stored directly in breadcrumb data or derived from
// the materialid there. If breadcrumb data is null, we probably got here by
// selecting a node on the model directly so check there
// note that with extended sidebar open we only ever have one selected style

const getActiveDesignCategory = createSelector(
  getBreadcrumbData,
  getUniqueSelectedElementStyles,
  (breadcrumbData, selectedElementStyles) => {
    if (Object.values(breadcrumbData).length > 0) {
      const { category, materialId } = breadcrumbData;
      if (category) {
        return category;
      }
      if (materialId) {
        return MATERIALS[materialId].category;
      }
    } else if (selectedElementStyles) {
      return Object.values(selectedElementStyles)[0].category;
    }
    return null;
  },
);

// does the opposite of getNextActiveAttribute. in the attribute editing 'flow' will return the value of
// the step previous to the currently selected attribute. If there is no previous step it returns null

const getPreviousActiveAttribute = createSelector(
  getActiveEditingAttributeIndex,
  getActiveEditingAttribute,
  getActiveDesignCategory,
  getActiveAttributes,
  (
    activeAttributeIndex,
    activeEditingAttribute,
    activeDesignCategory,
    activeAttributes,
  ) => {
    const currentAttribute = activeEditingAttribute.property;

    let previousAttribute = null;
    // if previousAttribute is null, the extended sidebar will close
    // we want to do this at top level (attribute index 0) or if
    // we're in brandId (index 1) and category is not walls

    if (
      (activeAttributeIndex === 1 && activeDesignCategory === 'walls') ||
      activeAttributeIndex > 1
    ) {
      previousAttribute = activeAttributes[activeAttributeIndex - 1].property;
    }

    return previousAttribute;
  },
);

/**
 * Returns hash of material IDs grouped by category used to build
 * the "All Categories" menu:
 * {
 *   [categoryName_1]: {
 *     [materialId_1]: ...
 *     [materialId_2]: ...
 *   },
 *   [categoryName_2]: ...
 * }
 *
 * Each material ID object contains 4 properties:
 * - name
 * - id (material/label ID)
 * - colorHex: original colorHex or selected variation colorHex
 * - imageUrl: original texture's image selected product's image
 */

const getModelMaterialsByCategory = createSelector(
  getNodeStyles,
  getLabels,
  getProducts,
  getMacheteVariations,
  getMacheteProducts,
  (styles = [], labels, products, variations, macheteProducts) => {
    const modelMaterialsByCategory = styles.reduce((acc, style) => {
      const {
        category,
        materialId,
        materialColorHex,
        materialTextureId,
        productId,
        colorId,
        paintColorId,
      } = style;
      const material = labels[materialId];

      // dont show button for no material or for glass elements
      if (!material || material.id === GLASS_ID) return acc;

      if (!acc[category]) acc[category] = {};

      const { name, id, imageUrl: materialImageUrl } = material;
      let colorHex = materialColorHex;
      let texture;
      let imageUrl;

      if (materialTextureId) {
        texture = products[materialTextureId];
      } else if (productId) {
        texture = macheteProducts[productId];
      }

      imageUrl =
        lodashGet(texture, 'texture.thumbnail.url') || materialImageUrl;

      if (colorId || paintColorId) {
        const colorIdForThumbnail = paintColorId || colorId;
        const variation = variations[colorIdForThumbnail] || {};
        colorHex = variation.color;
        const variationImageUrl = lodashGet(variation, 'texture.thumbnail.url');
        if (variationImageUrl) imageUrl = variationImageUrl;
      }

      acc[category][materialId] = {
        name,
        id,
        imageUrl,
        colorHex,
      };

      return acc;
    }, {});

    return Object.values(CATEGORIES)
      .filter((categoryName) => !!modelMaterialsByCategory[categoryName])
      .map((categoryName) => [
        categoryName,
        modelMaterialsByCategory[categoryName],
      ]);
  },
);

export default {
  generateStyleKey,
  getActiveAttributes,
  getActiveDesignCategory,
  getActiveEditingAttribute,
  getActiveEditingAttributePropertyId,
  getActiveEditingStyle,
  getActiveMode,
  getActiveRenderMode,
  getActiveVariationGroupIndex,
  getActiveVariationGroups,
  getBreadcrumbData,
  getColorByIdProp,
  getColorsByProductId,
  getColorSelectionCount,
  getDataForAttributeSelectionButton,
  getDesignProFeatureAccesses,
  getDesignProFlipperFlags,
  getErrorModalType,
  getExtendedSidebarData,
  getIsEditing3DOpening,
  getIsScreenshotModeActive,
  getJobDetails,
  getMacheteProducts,
  getMacheteVariationGroups,
  getMacheteVariations,
  getValidMaterials,
  getMaterialsWithBrands,
  getModelMaterialsByCategory,
  getNextActiveAttribute,
  getNodeStyles,
  getNodeStylesMap,
  getPaintBrands,
  getPaintColorByIdProp,
  getPreviousActiveAttribute,
  getProductByIdProp,
  getProductsByBrandAndMaterial,
  getSelectedNodeStyles,
  getShareToken,
  getTextForPaintAttribute,
  getTextForStyleAttribute,
  getUniqueSelectedElementStyles,
  isDesignModeActive,
  isProductCatalogInitialized,
  isSelectToolActive,
  isSelectionAvailable,
  shouldShowDesignProSpinner,
  getOrgFeatureAccess,
};
